// interview-answer-option-id-resolution.ts — V2-1 (spec V2 §4.1, E3).
//
// Chuẩn hoá giá trị trả lời của câu `single`/`multiple` về thứ được LƯU:
//  - câu có `option_ids` (worker V2 gửi id hợp lệ): nhận option id; nhận cả
//    nhãn nếu nhãn đó ánh xạ DUY NHẤT về một id ⇒ lưu id. Nhãn trùng (hai
//    option cùng nhãn khác id) ⇒ từ chối, chỉ id mới phân biệt được;
//  - câu v1 (không `option_ids`): giá trị phải là một nhãn trong `options` —
//    đúng luật cũ của `recordInterviewAnswer`.
import type { InterviewQuestion } from "./schemas/interview-schema.js";

export type ResolvedAnswerValues = { ok: true; values: string[] } | { ok: false; reason: string };

function resolveOne(question: InterviewQuestion, value: string): string | { reason: string } {
  const ids = question.option_ids ?? [];
  const labels = question.options ?? [];
  if (ids.includes(value)) return value;
  const at = labels.flatMap((label, i) => (label === value ? [i] : []));
  if (at.length === 1) return ids[at[0] as number] as string;
  if (at.length > 1) {
    return {
      reason: `nhãn "${value}" trùng ở ${at.length} lựa chọn của câu "${question.id}" — trả lời bằng option id (${at.map((i) => ids[i]).join(", ")}).`,
    };
  }
  return {
    reason: `giá trị ${value} ngoài options của câu "${question.id}" (option id: ${ids.join(", ")}; nhãn: ${labels.join(", ")}).`,
  };
}

export function resolveAnswerValues(
  question: InterviewQuestion,
  values: string[],
): ResolvedAnswerValues {
  if (question.option_ids === undefined) {
    const allowed = new Set(question.options ?? []);
    const invalid = values.filter((v) => !allowed.has(v));
    return invalid.length === 0
      ? { ok: true, values }
      : {
          ok: false,
          reason: `giá trị ${invalid.join(", ")} ngoài options của câu "${question.id}" (${[...allowed].join(", ")}).`,
        };
  }
  const resolved: string[] = [];
  for (const value of values) {
    const one = resolveOne(question, value);
    if (typeof one !== "string") return { ok: false, reason: one.reason };
    resolved.push(one);
  }
  return { ok: true, values: resolved };
}

/** Nhãn hiển thị của một option id; `undefined` khi câu không có id hoặc id lạ. */
export function optionLabelForId(question: InterviewQuestion, id: string): string | undefined {
  const index = question.option_ids?.indexOf(id) ?? -1;
  return index >= 0 ? question.options?.[index] : undefined;
}
